import { createContext, useContext, type ReactNode } from 'react'

/**
 * AB#2598 — the host seam for "create a new option without leaving the form".
 *
 * A field declares `lookupField.allowCreate`; the HOST decides what creating means for that source and
 * returns the value to select. Resolving to undefined means the user backed out and the field is left
 * untouched.
 *
 * Deliberately a CONTEXT rather than another prop on the renderer. `fetchEntityOptions` and
 * `fetchEntityById` are prop-drilled through 28 call sites in ViewRenderer, and every one is a chance to
 * miss a branch — a seam that silently does not arrive in nested grids or tab slots would show the
 * affordance in some places and not others, which is worse than not having it. Exactly one leaf consumes
 * this, so it travels straight there. A host that provides nothing (the designer preview) offers no
 * create row, which is the correct behaviour there: the preview cannot create anything.
 */
export interface LookupCreateRequest {
    /** The lookup's `sourceEntity` — the host dispatches on this. */
    sourceEntity: string
    /** The in-screen company, when the view has one. A company-scoped create needs it. */
    companyId?: string | undefined
    /** The field's declared `optionFilters`, so what is created matches what the picker will offer. */
    optionFilters?: Record<string, string | number | boolean> | undefined
}

export type LookupCreateHandler = (request: LookupCreateRequest) => Promise<string | undefined>

const LookupCreateContext = createContext<LookupCreateHandler | undefined>(undefined)

export function LookupCreateProvider(
    { handler, children }: { handler: LookupCreateHandler; children: ReactNode },
) {
    return <LookupCreateContext.Provider value={handler}>{children}</LookupCreateContext.Provider>
}

/** The host's create handler, or undefined when the host provides none. */
export function useLookupCreate(): LookupCreateHandler | undefined {
    return useContext(LookupCreateContext)
}
